// =============================================================================
// Delegation of Approvals — TypeScript Interfaces & Types
// PRD: Section 4, 5, 7, 9
// =============================================================================

// ---------------------------------------------------------------------------
// Status & Enum Types
// ---------------------------------------------------------------------------

/**
 * Full lifecycle of a delegation record.
 *
 * State machine:
 *   active → revoked  (manager/admin revokes)
 *   active → expired  (auto-expiry job, end date passed)
 *
 * Delegations are always active immediately upon creation.
 * There is no acceptance step.
 */
export type DelegationStatus =
  | 'active'
  | 'expired'
  | 'revoked';

/**
 * Reason codes an approver may give when creating a delegation.
 */
export type DelegationReason =
  | 'annual_leave'
  | 'sick_leave'
  | 'parental_leave'
  | 'conference'
  | 'business_travel'
  | 'other';

/**
 * Scope of what the delegate can action on behalf of the manager.
 * Currently "all" is the only supported scope; kept for future extensibility.
 */
export type DelegationScope = 'all' | 'specific_projects';

/**
 * Eligibility rules applied when selecting a delegate.
 */
export type DelegateEligibilityRule =
  | 'must_have_approval_permission'
  | 'same_company'
  | 'same_department';

// ---------------------------------------------------------------------------
// Core DTO — shape returned by all GET endpoints
// ---------------------------------------------------------------------------

/**
 * Full delegation record as returned by the API.
 * Matches PRD §9.2 response shape.
 */
export interface ApprovalDelegation {
  id: string;
  managerId: string;
  managerName: string;
  managerEmail?: string;
  delegateId: string;
  delegateName: string;
  delegateEmail?: string;
  startDate: string; // ISO date string "YYYY-MM-DD"
  endDate: string;   // ISO date string "YYYY-MM-DD"
  status: DelegationStatus;
  reason?: DelegationReason;
  scope: DelegationScope;
  notes?: string;
  createdAt: string; // ISO datetime
  createdBy: string;
  updatedAt?: string;
  revokedAt?: string;
  revokedBy?: string;
  revokeReason?: string;
  // Computed / metadata
  durationDays: number;
  // Audit
  auditEvents?: DelegationAuditEntry[];
}

// ---------------------------------------------------------------------------
// Request Interfaces
// ---------------------------------------------------------------------------

export interface CreateDelegationRequest {
  delegateId: string;
  startDate: string; // "YYYY-MM-DD"
  endDate: string;   // "YYYY-MM-DD"
  scope?: DelegationScope;
  notes?: string;
}

export interface UpdateDelegationRequest {
  endDate?: string;
  notes?: string;
}

export interface RevokeDelegationRequest {
  reason?: string;
}

// ---------------------------------------------------------------------------
// Response Interfaces
// ---------------------------------------------------------------------------

export interface RevokeDelegationResponse {
  delegation: ApprovalDelegation;
  message: string;
}

export interface ForceExpireResponse {
  delegation: ApprovalDelegation;
  message: string;
}

// ---------------------------------------------------------------------------
// Audit Trail
// ---------------------------------------------------------------------------

/**
 * A single event in a delegation's lifecycle.
 */
export interface DelegationAuditEntry {
  id: string;
  delegationId: string;
  event:
    | 'created'
    | 'revoked'
    | 'expired'
    | 'action_taken'
    | 'updated';
  actorId: string;
  actorName: string;
  notes?: string;
  occurredAt: string; // ISO datetime
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Admin Audit Report
// ---------------------------------------------------------------------------

export interface AdminDelegationListItem {
  delegation: ApprovalDelegation;
  events: DelegationAuditEntry[];
}

export interface AdminDelegationListResponse {
  items: AdminDelegationListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface DelegationAuditFilter {
  managerId?: string;
  delegateId?: string;
  status?: DelegationStatus | 'all';
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * Organisation-wide delegation settings (Feature A, PRD §4).
 */
export interface DelegationSettings {
  delegationEnabled: boolean;
  allowSelfDelegation: boolean;
  maxDelegationDays: number;
  allowOverlappingDelegations: boolean;
  allowCascadeDelegation: boolean;
  requireNotes: boolean;
  notifyManagerOnDelegateAction: boolean;
  notifyDelegateOnAssignment: boolean;
  notifyDelegateOnStart: boolean;
  notifyManagerBeforeExpiry: boolean;
  expiryReminderHoursBefore: number;
  eligibilityRequireApprovalPermission: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * PRD §4 default values for all delegation settings.
 */
export const DEFAULT_DELEGATION_SETTINGS: DelegationSettings = {
  delegationEnabled: true,
  allowSelfDelegation: false,
  maxDelegationDays: 90,
  allowOverlappingDelegations: false,
  allowCascadeDelegation: false,
  requireNotes: false,
  notifyManagerOnDelegateAction: true,
  notifyDelegateOnAssignment: true,
  notifyDelegateOnStart: true,
  notifyManagerBeforeExpiry: true,
  expiryReminderHoursBefore: 24,
  eligibilityRequireApprovalPermission: true,
};

// ---------------------------------------------------------------------------
// Validation & Error Types
// ---------------------------------------------------------------------------

/**
 * Structured error codes returned by the API (PRD §5 & §9.2).
 */
export type DelegationErrorCode =
  | 'self_delegation'
  | 'invalid_date_range'
  | 'overlap_conflict'
  | 'circular_delegation'
  | 'cascade_not_allowed'
  | 'delegate_not_eligible'
  | 'exceeds_max_duration'
  | 'delegation_disabled'
  | 'not_found'
  | 'already_actioned'
  | 'forbidden';

/**
 * Structured error returned by delegation API endpoints.
 */
export interface DelegationApiError {
  error: DelegationErrorCode;
  message: string;
  conflictingDelegation?: ApprovalDelegation; // present on overlap_conflict (409)
}

/**
 * Client-side validation result before hitting the API.
 */
export interface DelegationValidationResult {
  valid: boolean;
  errors: Array<{
    field: 'delegateId' | 'startDate' | 'endDate' | 'notes' | 'general';
    code: DelegationErrorCode;
    message: string;
  }>;
}

/**
 * Reason option for the create form dropdown.
 */
export interface DelegationReasonOption {
  value: DelegationReason;
  label: string;
}

export const DELEGATION_REASON_OPTIONS: DelegationReasonOption[] = [
  { value: 'annual_leave', label: 'Annual Leave' },
  { value: 'sick_leave', label: 'Sick Leave' },
  { value: 'parental_leave', label: 'Parental Leave' },
  { value: 'conference', label: 'Conference / Event' },
  { value: 'business_travel', label: 'Business Travel' },
  { value: 'other', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Reassign Pending Timesheets (PRD §4 Feature B)
// ---------------------------------------------------------------------------

/**
 * Response from GET /api/approvals/pending/count (BE-22).
 * Used to decide whether to show the reassign prompt on the confirmation screen.
 */
export interface PendingTimesheetCountResponse {
  pendingCount: number;
}

/**
 * Response from POST /api/delegations/{id}/reassign-pending (BE-23).
 */
export interface ReassignPendingResponse {
  reassignedCount: number;
}

// ---------------------------------------------------------------------------
// Eligible Delegate (lightweight shape for delegate picker)
// ---------------------------------------------------------------------------

/**
 * Lightweight employee record for the delegate selection dropdown.
 * Returned by DelegationService.getEligibleDelegates().
 */
export interface EligibleDelegate {
  id: string;
  name: string;
  email?: string;
}

// ---------------------------------------------------------------------------
// UI State / Filter Types
// ---------------------------------------------------------------------------

export type DelegationStatusFilter = DelegationStatus | 'all' | 'upcoming';

export interface ApprovalQueueFilter {
  showOwn: boolean;
  showDelegated: boolean;
  managerId?: string; // filter delegated items to a specific manager
}

// ---------------------------------------------------------------------------
// Approval response enrichment (for FE-14 / FE-15)
// Already exists on ApprovalResponse model in backend — mirrored here for UI
// ---------------------------------------------------------------------------

export interface DelegationApprovalMetadata {
  isDelegated: boolean;
  delegatedFromManagerId?: string;
  delegatedFromManagerName?: string;
  delegationId?: string;
}
